// src/cockpit4/components/RealTimeSearchPanel.tsx
// ============================================
// ① 实时搜索验证 - 输入问题，看 AI 引用了谁
//
// 功能：输入「想验证的问题 + 品牌名」，逐平台轮询 6 大 AI（豆包/文心/通义/元宝/DeepSeek/Kimi），
//       实时返回每个 AI 是否引用该品牌、引用位次、引用片段，并给出「谁引用了你」的排名。
//
// 数据来源（真实，无 mock）：
//   · 走 GEODiagnosticRuntime（服务端代理 /{platform}-api 真实 API + DeepSeek 兜底）
//   · 品牌实体词优先从 Obsidian 品牌圣经解析，否则用输入词
// ============================================

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Send,
  CheckCircle2,
  XCircle,
  Trophy,
  Quote,
  RefreshCw,
  Activity,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { DIAG_PLATFORMS, DIAG_PLATFORM_MAP } from "../../runtime/GrowthFactory/geoPlatforms";
import DiagPlatformSelector from "./DiagPlatformSelector";
import type {
  RealTimeSearchResult,
  DiagPlatformKey,
} from "../../runtime/GrowthFactory/types";
import type { GEODiagnosticRuntime } from "../../runtime/GrowthFactory/GEODiagnosticRuntime";

type Stage = "idle" | "running" | "done" | "error";

export default function RealTimeSearchPanel() {
  const [question, setQuestion] = useState("");
  const [brand, setBrand] = useState("小舍科技");
  const [selected, setSelected] = useState<DiagPlatformKey[]>(
    DIAG_PLATFORMS.map((p) => p.key)
  );
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState<RealTimeSearchResult | null>(null);
  const [error, setError] = useState("");
  const abortRef = useRef(false);

  const getRuntime = () =>
    RuntimeManager.getRuntime(RuntimeName.GEODiagnostic) as unknown as GEODiagnosticRuntime;

  const runSearch = async () => {
    const q = question.trim();
    if (!q) {
      setError("请输入想验证的问题");
      setStage("error");
      return;
    }
    if (selected.length === 0) {
      setError("请至少勾选 1 个诊断平台");
      setStage("error");
      return;
    }
    abortRef.current = false;
    setError("");
    setResult(null);
    setStage("running");
    try {
      const data = await getRuntime().diagnoseRealTime(
        q,
        brand.trim(),
        selected,
        (msg) => setProgress(msg)
      );
      if (abortRef.current) return;
      setResult(data);
      setStage("done");
    } catch (e: any) {
      if (abortRef.current) return;
      setError(e?.message || String(e));
      setStage("error");
    }
  };

  const mentionRate = result ? result.mentionRate : 0;
  const gradeColor =
    mentionRate >= 0.6 ? "#34D399" : mentionRate >= 0.4 ? "#FBBF24" : "#F87171";

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 顶栏 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Search size={16} className="text-cyan-400" />
            <span className="text-[13px] font-semibold text-white tracking-tight">
              ① 实时搜索验证
            </span>
          </div>
          <p className="text-[10px] font-mono text-gray-500 mt-1">
            输入问题 → 实时询问 6 大 AI → 看谁引用了你的品牌 / 引用位次 / 引用原文
          </p>
        </div>
      </div>

      {/* 输入区 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-mono text-gray-500">验证问题</label>
          <div className="flex items-center gap-2">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="例如：酒店想上自助入住机，有哪些落地案例或方案？"
              className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-cyan-500/50"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-mono text-gray-500">要验证的品牌 / 公司（留空则验证知识库中的品牌）</label>
          <div className="flex items-center gap-2">
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="例如：小舍科技"
              className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-cyan-500/50"
            />
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={runSearch}
              disabled={stage === "running"}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-medium
                         bg-cyan-500/15 text-cyan-300 border border-cyan-500/30
                         hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
            >
              {stage === "running" ? (
                <RefreshCw size={13} className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              {stage === "running" ? "验证中…" : "开始验证"}
            </motion.button>
          </div>
        </div>
        {error && <div className="text-[11px] text-red-400">{error}</div>}
      </div>

      {/* 平台勾选区：已接入 6 大 AI 厂家，勾选后轮询 */}
      <DiagPlatformSelector selected={selected} onChange={setSelected} />

      {progress && (
        <div className="text-[11px] font-mono text-cyan-300/80">
          <Activity size={12} className="inline mr-1" />
          <span className="text-cyan-400">▸</span> {progress}
        </div>
      )}

      {stage === "idle" && (
        <div className="text-[11px] text-gray-500 py-8 text-center border border-dashed border-white/[0.06] rounded-lg">
          输入一个「行业提问」，勾选要验证的 AI 厂家，点击开始验证，实时看哪些 AI 引用了你的品牌、引用的位次和原文片段。
        </div>
      )}

      {result && (
        <>
          {/* 汇总 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight" style={{ color: gradeColor }}>
                {Math.round(mentionRate * 100)}%
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">引用率</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.mentionCount}/{result.total} 个 AI 引用了品牌
              </div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-amber-400">
                {result.firstCount}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">首推 AI 数</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">回答开头即推荐品牌</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-cyan-400">
                {result.ranking.length}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">引用排名 AI 数</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">按提及次数排序</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[14px] font-bold tracking-tight text-white truncate">
                {result.question?.slice(0, 14) || "-"}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">验证问题</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.checkedAt?.slice(11, 19) || ""} 执行
              </div>
            </div>
          </div>

          {/* 谁引用了你（排名） */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-amber-400" />
              <span className="text-[12px] font-semibold text-white">谁引用了你（排名）</span>
              <span className="text-[9px] font-mono text-gray-600">按提及次数 + 位次综合排序</span>
            </div>
            {result.ranking.length === 0 ? (
              <div className="text-[11px] text-gray-500 py-3 text-center">
                没有 AI 引用该品牌 —— 说明该问题场景下品牌不可见，需铺设 GEO 内容。
              </div>
            ) : (
              result.ranking.map((r, idx) => {
                const p = DIAG_PLATFORM_MAP[r.platform as DiagPlatformKey];
                return (
                  <div key={r.platform} className="flex items-center gap-3 py-1.5">
                    <span className="w-5 text-center">
                      {idx === 0 ? (
                        <Trophy size={13} style={{ color: "#F59E0B" }} />
                      ) : (
                        <span className="text-[10px] font-mono text-gray-600">{idx + 1}</span>
                      )}
                    </span>
                    <span
                      className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold text-white"
                      style={{ background: p.color }}
                    >
                      {p.short}
                    </span>
                    <span className="w-20 text-[11px] text-gray-300">{p.name}</span>
                    <span className="text-[11px] font-mono text-gray-400">
                      {r.count} 次提及{r.first ? " · 首推" : ""}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* 各平台明细 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <Quote size={14} className="text-cyan-400" />
              <span className="text-[12px] font-semibold text-white">各 AI 引用明细</span>
              <span className="text-[9px] font-mono text-gray-600">
                展开查看 AI 原始回答
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {result.results.map((r) => {
                const p = DIAG_PLATFORM_MAP[r.platform as DiagPlatformKey];
                return (
                  <div
                    key={r.platform}
                    className={`rounded-md border p-2.5 ${
                      r.mentioned
                        ? "bg-emerald-500/[0.04] border-emerald-500/20"
                        : "bg-white/[0.01] border-white/[0.06]"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ background: p.color }}
                      >
                        {p.short}
                      </span>
                      <span className="text-[11px] font-medium text-white flex-1">{p.name}</span>
                      {r.mentioned ? (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <CheckCircle2 size={12} />
                          {r.first ? "首推" : r.top3 ? "前3推荐" : "已引用"}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] text-gray-500">
                          <XCircle size={12} />
                          未引用
                        </span>
                      )}
                    </div>
                    {r.mentioned && (
                      <>
                        <div className="text-[10px] font-mono text-gray-500 mb-1">
                          位次: 第 {Math.max(1, Math.floor(r.position / 100) + 1)} 段 · 正文提及 {r.count} 次
                        </div>
                        <div className="text-[11px] text-gray-300 leading-relaxed bg-black/20 rounded p-2 border border-white/[0.04]">
                          “…{r.snippet}…”
                        </div>
                      </>
                    )}
                    {r.error && (
                      <div className="text-[10px] text-red-400/80">{r.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
